import type { matterTeamFormSchema } from "@/components/features/settings/defaults/matter-teams/matterTeamFormSchema";
import {
  ActionConfirmationModal,
  Button,
  Form,
  FormSubmitButton,
  SearchableSelectFormField,
  SelectFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  useCreateMatterTeammate,
  useDeleteMatterTeammate,
  useUpdateMatterTeammate,
} from "@/lib/data/react-query/matters/matter-teammate";

import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterRole, type MatterTeammate } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { z, type z as zType } from "zod";

interface AddOrEditMatterTeammateModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  id: string;
  parentForm?: UseFormReturn<zType.infer<typeof matterTeamFormSchema>>;
}

type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      activeMatterTeammate: Pick<
        MatterTeammate,
        "id" | "dateModified" | "roleId" | "userId"
      > &
        Partial<MatterTeammate>;
    }
  | {
      isReadOnly?: never;
      editing: false;
      activeMatterTeammate?: never;
    };

const formSchema = z.object({
  userId: z.string(),
  matterRoleId: z.string(),
});

const defaultFormValues = {
  userId: "",
  matterRoleId: "",
} as const;

const FORM_ID = "matter-teammate-form";

export function AddOrEditMatterTeammateModal({
  open,
  setOpen,
  activeMatterTeammate,
  id,
  editing,
  isReadOnly,
  parentForm,
}: AddOrEditMatterTeammateModalProps & ConditionalProps) {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          userId: activeMatterTeammate.userId,
          matterRoleId: activeMatterTeammate.roleId,
        }
      : defaultFormValues,
    mode: "onSubmit",
  });

  const createMatterTeammate = useCreateMatterTeammate();
  const updateMatterTeammate = useUpdateMatterTeammate();
  const deleteMatterTeammate = useDeleteMatterTeammate();
  const isFormLoading =
    createMatterTeammate.isPending ||
    updateMatterTeammate.isPending ||
    deleteMatterTeammate.isPending;

  const userDirectory = useUserDirectory({
    enabled: open,
    selectedIds: [form.watch("userId")],
  });
  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );

  const matterRoles = matterSettingsData?.data.roles ?? [];
  const users = userDirectory.users;

  const onSubmitParentForm = (data: z.infer<typeof formSchema>) => {
    if (!parentForm) return;
    const existingTeammates = parentForm.getValues("newMatterTeammates");
    parentForm.setValue(
      "newMatterTeammates",
      [
        ...existingTeammates,
        {
          id: data.userId,
          matterRoleId: data.matterRoleId,
          matterTeammateId: Math.random().toString(),
        },
      ],
      { shouldDirty: true },
    );
  };

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (parentForm) {
      onSubmitParentForm(data);
      setOpen(false);
      form.reset();
      return;
    }

    const user = users.find((user) => user.id === data.userId);
    setOpen(false);
    if (editing) {
      updateMatterTeammate.mutate({
        id: activeMatterTeammate.id,
        teamId: id,
        roleId: data.matterRoleId,
        userId: data.userId,
        dateModified: activeMatterTeammate.dateModified,
      });
    } else {
      if (user) {
        createMatterTeammate.mutate({
          roleId: data.matterRoleId,
          teamId: id,
          user: { userId: user.id },
        });
      }
    }
    form.reset();
  };

  const sheetTitle = useMemo(
    () =>
      isReadOnly
        ? "View matter teammate"
        : editing
          ? "Update matter teammate"
          : "Add teammate",
    [isReadOnly, editing],
  );

  const sheetDescription = useMemo(
    () =>
      isReadOnly
        ? "View the matter teammate details below"
        : editing
          ? "Update the matter teammate details below"
          : "Add a new teammate to this team",
    [isReadOnly, editing],
  );

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const canDelete =
    !isReadOnly &&
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterTeammate,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = canDelete ? (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={sheetTitle}
          description={sheetDescription}
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size="xl"
          overlayClassName="z-[108]"
          className="z-[109]"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  isDirty={form.formState.isDirty}
                  setOpen={setOpen}
                />
                {!isReadOnly && (
                  <FormSubmitButton
                    submitFormId={FORM_ID}
                    title={editing ? "Save" : "Add"}
                  />
                )}
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <fieldset className={slideoverFieldsetClassName}>
                <SearchableSelectFormField
                  label="Assignee"
                  items={users}
                  itemsKey="id"
                  formatItemLabel={(item) => formatUsersName(item) ?? ""}
                  register={form.register("userId")}
                  placeholder="Select an assignee"
                  {...userDirectory.selectProps}
                />
                <SelectFormField<MatterRole>
                  label="Role"
                  items={matterRoles}
                  itemsKey="id"
                  formatItemLabel={(item) => item?.label}
                  register={form.register("matterRoleId")}
                  placeholder="Select a role"
                />

                <div className="col-span-full"></div>
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title={`Are you sure you want to delete this matter teammate?`}
        open={isDeleting && !!activeMatterTeammate}
        setOpen={setIsDeleting}
        afterActionFn={() => {
          if (activeMatterTeammate) {
            deleteMatterTeammate.mutate({
              id: activeMatterTeammate.id,
              teamId: id,
            });
          }
        }}
      />
    </>
  );
}
